import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brand } from '../components/Brand';
import { BrainOrb, BrainPrompt, Button, Card, GlassPill } from '../components/primitives';
import { useAuth } from '../contexts/AuthContext';
import { useActiveTeam } from '../lib/queries/useActiveTeam';
import { useVoiceToText } from '../lib/voiceInput';
import {
  GROWTH_QUESTIONS,
  advanceGrowthCheckin,
  createInitialGrowthCheckin,
  currentGrowthQuestion,
  saveGrowthCheckin,
} from '../lib/growthCheckin';
import type { GrowthCheckinState } from '../lib/growthCheckin';
import './CoachGrowthCheckIn.css';

interface ConversationTurn {
  id: string;
  role: 'coach' | 'brain';
  text: string;
}

export function CoachGrowthCheckIn() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { team } = useActiveTeam();
  const [state, setState] = useState<GrowthCheckinState>(() => createInitialGrowthCheckin());
  const [turns, setTurns] = useState<ConversationTurn[]>(() => [{
    id: 'first',
    role: 'brain',
    text: GROWTH_QUESTIONS[0].prompt,
  }]);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = useCallback(async (text: string) => {
    const clean = text.trim();
    if (!clean || done) return;

    const next = advanceGrowthCheckin(state, clean);
    setState(next);
    setTurns((existing) => {
      const withCoach = [...existing, { id: crypto.randomUUID(), role: 'coach' as const, text: clean }];
      const nextQuestion = currentGrowthQuestion(next);
      if (nextQuestion) {
        return [...withCoach, { id: crypto.randomUUID(), role: 'brain' as const, text: nextQuestion.prompt }];
      }
      return [...withCoach, {
        id: crypto.randomUUID(),
        role: 'brain' as const,
        text: 'Got it. Saving this as a Brain prior — every answer Brain gives you for the next fortnight will weigh against this.',
      }];
    });

    if (next.finished && user) {
      setSaving(true);
      const result = await saveGrowthCheckin({
        coachId: user.id,
        teamId: team?.id ?? null,
        answers: next.answers,
      });
      if (result.ok) {
        setSaveMessage('Saved.');
        setDone(true);
      } else {
        setSaveMessage(result.error);
      }
      setSaving(false);
    }
  }, [state, user, team, done]);

  const voice = useVoiceToText({ onTranscript: submit });
  const totalSteps = GROWTH_QUESTIONS.length;
  const stepLabel = done
    ? 'complete'
    : `${Math.min(state.step + 1, totalSteps)} of ${totalSteps}`;

  return (
    <main className="growth-checkin" aria-label="Coach growth check-in">
      <header className="growth-checkin__top">
        <Brand team={team?.name ?? 'Coach growth'} />
        <GlassPill mono>Layer 3 · {stepLabel}</GlassPill>
      </header>

      <section className="growth-checkin__panel">
        <div className="growth-checkin__head">
          <BrainOrb
            size="lg"
            state={voice.listening ? 'listening' : saving ? 'thinking' : 'idle'}
            ariaLabel="Brain"
            className="growth-checkin__orb"
          />
          <span className="growth-checkin__eyebrow">Coach growth check-in</span>
          <h1 className="growth-checkin__title">{done ? 'See you in two weeks.' : 'Talk it through.'}</h1>
        </div>

        <div className="growth-checkin__thread" aria-live="polite">
          {turns.map((turn) => (
            <div key={turn.id} className={`growth-checkin__turn growth-checkin__turn--${turn.role}`}>
              <span className="growth-checkin__turn-speaker">
                {turn.role === 'brain' && <BrainOrb size="xs" ariaLabel="Brain" />}
                {turn.role === 'brain' ? 'Brain' : 'Coach'}
              </span>
              <p>{turn.text}</p>
            </div>
          ))}
        </div>

        {!done && (
          <BrainPrompt
            hint="hold the orb or type your answer"
            placeholder="say what comes to mind"
            listening={voice.listening}
            voiceStatus={voice.status}
            onMicStart={() => void voice.start()}
            onMicStop={voice.stop}
            onSubmit={submit}
          />
        )}

        {done && (
          <Card variant="glass" className="growth-checkin__done">
            <p>Brain has the four answers. Open Home or Reviews to keep going.</p>
            <div className="growth-checkin__done-actions">
              <Button variant="primary" size="touchline" onClick={() => navigate('/')}>Back to Home</Button>
              <Button variant="glass" size="touchline" onClick={() => navigate('/reviews')}>See past check-ins</Button>
            </div>
          </Card>
        )}

        {saveMessage && <p className="growth-checkin__status">{saveMessage}</p>}
      </section>
    </main>
  );
}
